import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { en, type Dictionary } from './en'
import { fr } from './fr'
import { de } from './de'
import { es } from './es'
import { it } from './it'
import { nl } from './nl'
import { pt } from './pt'
import { sv } from './sv'
import { pl } from './pl'
import { ja } from './ja'
import { ko } from './ko'
import { zh } from './zh'

/**
 * Traductions.
 *
 * Chaque dictionnaire est déclaré `satisfies Dictionary` : une clé manquante ou
 * mal orthographiée casse le build. C'est la seule façon d'éviter la dérive
 * habituelle où une langue accumule silencieusement des phrases en anglais.
 */

export const LOCALES = {
  en: { label: 'English', dict: en },
  fr: { label: 'Français', dict: fr },
  de: { label: 'Deutsch', dict: de },
  es: { label: 'Español', dict: es },
  it: { label: 'Italiano', dict: it },
  nl: { label: 'Nederlands', dict: nl },
  pt: { label: 'Português', dict: pt },
  sv: { label: 'Svenska', dict: sv },
  pl: { label: 'Polski', dict: pl },
  ja: { label: '日本語', dict: ja },
  ko: { label: '한국어', dict: ko },
  zh: { label: '简体中文', dict: zh },
} as const

export type Locale = keyof typeof LOCALES

const STORAGE_KEY = 'locale'

function detect(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && saved in LOCALES) return saved as Locale
  } catch { /* stockage indisponible : on suit le navigateur */ }

  // `navigator.languages` est ordonné par préférence : on prend la première
  // langue que l'on sait parler, plutôt que la première tout court.
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split('-')[0]
    if (base && base in LOCALES) return base as Locale
  }
  return 'en'
}

interface I18nValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: Dictionary
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en')

  // La détection lit `navigator` et `localStorage` : après le montage seulement,
  // pour rester compatible avec un rendu statique.
  useEffect(() => { setLocaleState(detect()) }, [])

  useEffect(() => { document.documentElement.lang = locale }, [locale])

  const value = useMemo<I18nValue>(() => ({
    locale,
    setLocale: (next) => {
      try { localStorage.setItem(STORAGE_KEY, next) } catch { /* sans mémoire, tant pis */ }
      setLocaleState(next)
    },
    t: LOCALES[locale].dict,
  }), [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n doit être utilisé dans un I18nProvider')
  return context
}

/** Raccourci pour le cas courant : on ne veut que les textes. */
export function useT(): Dictionary {
  return useI18n().t
}
